import { useEffect } from 'react'
import { Routes, Route } from 'react-router-dom'
import { useAppStore } from '@/store'
import AdminLayout from './admin/components/AdminLayout'
import EventsPage from './admin/pages/EventsPage'
import EventEditor from './admin/pages/EventEditor'
import SettingsPage from './admin/pages/SettingsPage'
import ReportsPage from './admin/pages/ReportsPage'
import GlobalPosesPage from './admin/pages/GlobalPosesPage'
import GlobalMonitor from './admin/pages/GlobalMonitor'
import LoginPage from './admin/pages/LoginPage'
import BoothApp from './booth/BoothApp'
import LandingPage from './LandingPage'
import HubPage from './HubPage'
import PublicGallery from './PublicGallery'
import KioskPage from './admin/pages/KioskPage'
import BoothErrorBoundary from './components/BoothErrorBoundary'
import RemoteControl from './admin/pages/RemoteControl'
import { useAuthStore } from '@/store'
import { Navigate, Outlet } from 'react-router-dom'
import UpdateNotice from './components/UpdateNotice'

function ProtectedRoute() {
  const { user, initialized } = useAuthStore()
  if (!initialized) return null
  if (!user) return <Navigate to="/login" replace />
  return <Outlet />
}

function ThemeSync() {
  const darkMode = useAppStore((s) => s.darkMode)
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
  }, [darkMode])
  return null
}

function App() {
  const checkAuth = useAuthStore((s) => s.checkAuth)
  const setUser = useAuthStore((s) => s.setUser)

  useEffect(() => {
    async function bootstrapAuth() {
      // Electron kiosk with a configured Application Password: sign in silently
      // instead of making the operator log in on-site.
      if (window.electronAPI?.isElectron?.()) {
        try {
          const settings = await window.electronAPI.getSettings()
          if (settings.apiKey) {
            const res = await fetch('/api/auth/key-login', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ key: settings.apiKey }),
            })
            if (res.ok) {
              const data = await res.json()
              setUser(data.user)
              return
            }
          }
        } catch (err) {
          console.error('Application password login failed:', err)
        }
      }
      checkAuth()
    }
    bootstrapAuth()
  }, [checkAuth, setUser])

  return (
    <>
      <ThemeSync />
      <UpdateNotice />
      <Routes>
        <Route element={<ProtectedRoute />}>
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<EventsPage />} />
            <Route path="events/:eventId/*" element={<EventEditor />} />
            <Route path="poses" element={<GlobalPosesPage />} />
            <Route path="reports" element={<ReportsPage />} />
            <Route path="monitor" element={<GlobalMonitor />} />
            <Route path="settings" element={<SettingsPage />} />
          </Route>
          <Route path="/admin/remote/:eventId" element={<RemoteControl />} />
          <Route path="/hub" element={<HubPage />} />
        </Route>
        
        <Route path="/login" element={<LoginPage />} />
        {/* Booth and kiosk run unattended for hours — wrap them so a render
            error self-recovers instead of leaving a blank screen all night. */}
        <Route
          path="/booth/:eventId"
          element={
            <BoothErrorBoundary>
              <BoothApp />
            </BoothErrorBoundary>
          }
        />
        <Route path="/booth" element={<div className="flex h-screen items-center justify-center text-white">No event specified. Use /booth/:eventId</div>} />
        <Route
          path="/kiosk/:eventId"
          element={
            <BoothErrorBoundary>
              <KioskPage />
            </BoothErrorBoundary>
          }
        />
        <Route path="/gallery" element={<PublicGallery />} />
        <Route path="/" element={<LandingPage />} />
      </Routes>
    </>
  )
}

export default App
